import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import React, { useEffect, useRef, useState } from "react";
import { auth, db } from "../firebase-config.js";
import config from "../json/config.json";
import "../styles/COLORS.css";
import "../styles/Chat.css";

library.add(fas);
export const Chat = (props) => {
  const currentUser = auth.currentUser;
  const { room } = props;
  const [newMessage, setNewMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [messageCooldown, setMessageCooldown] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [uniqueUsers, setUniqueUsers] = useState(0);
  const [offlineMessage, setOfflineMessage] = useState("");
  const messagesRef = collection(db, "messages");
  const messagesEndRef = useRef(null);

  const { chatConfig } = config;
  const { appConfig } = config;
  const { profileColorSeed } = appConfig;
  const { chatRoles } = config;
  const {
    maxWriteLength,
    messageCooldownLength,
    adminDeleteMessage,
    deleteMessage,
    deleteSymbol,
    adminDeleteSymbol,
    titleName,
    saveFileName,
  } = chatConfig;
  const { admins, vips, muted } = chatRoles;

  // Rollen ------------------------------------------
  const userEmail = auth.currentUser?.email || null;
  const isRoleAdmin = userEmail && admins.includes(userEmail);
  const isRoleVip = userEmail && vips.includes(userEmail);
  const isMuted = userEmail && muted.includes(userEmail);

  // --------------------------------------------------------- ------------------------------------------
  useEffect(() => {
    const auth = getAuth();
    onAuthStateChanged(auth, (user) => {
      setIsOnline(navigator.onLine && !!user);
    });

    window.addEventListener("online", handleOnlineStatusChange);
    window.addEventListener("offline", handleOnlineStatusChange);

    return () => {
      window.removeEventListener("online", handleOnlineStatusChange);
      window.removeEventListener("offline", handleOnlineStatusChange);
    };
  }, []);

  useEffect(() => {
    document.title = titleName + room;
    const queryMessages = query(
      messagesRef,
      where("room", "==", room),
      orderBy("createdAt")
    );
    const unsubscribe = onSnapshot(queryMessages, (snapshot) => {
      let messages = [];
      let users = new Set();
      snapshot.forEach((doc) => {
        const data = doc.data();
        messages.push({ ...data, id: doc.id });
        users.add(data.user);
      });
      setMessages(messages);
      setUniqueUsers(users.size);
      // scrollToTop();
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // scrollToBottom();
  }, []);

  // --------------------------------------------------------- Profilfarbe  ---------------------------------------------------------
  const ProfileColor = (username) => {
    const color = getComputedStyle(document.documentElement);
    const backgroundColors = Array.from({ length: 30 }, (_, i) =>
      color.getPropertyValue(`--color-${i + 1}`)
    );
    const hash = [...username].reduce(
      (acc, char) => acc + char.charCodeAt(0),
      profileColorSeed
    );

    const modifiedHash = (hash % 100) + 1;

    const colorIndex = modifiedHash % backgroundColors.length;

    return backgroundColors[colorIndex];
  };
  const personalColor = currentUser
    ? { color: ProfileColor(currentUser.displayName) }
    : {};
  // --------------------------------------------------------- Zeichenlimit ---------------------------------------------------------
  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    if (inputValue.length <= maxWriteLength) {
      setNewMessage(inputValue);
      setCharCount(inputValue.length);
    }
  };

  // --------------------------------------------------------- Nachricht senden ---------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newMessage === "" || messageCooldown || isMuted) return;

    setMessageCooldown(true);
    setTimeout(() => {
      setMessageCooldown(false);
    }, messageCooldownLength);

    setNewMessage("");

    await addDoc(messagesRef, {
      text: newMessage,
      createdAt: serverTimestamp(),
      user: auth.currentUser.displayName,
      uid: auth.currentUser.uid,
      userEmail: auth.currentUser.email,
      room,
    });

    setCharCount(0);
  };

  //  --------------------------------------------------------- Nachricht löschen ---------------------------------------------------------
  const handleDelete = async (messageId) => {
    const messageToDelete = messages.find(
      (message) => message.id === messageId
    );
    if (
      messageToDelete &&
      messageToDelete.user === auth.currentUser.displayName
    ) {
      if (messageCooldown) return;

      setMessageCooldown(true);
      setTimeout(() => {
        setMessageCooldown(false);
      }, messageCooldownLength);

      const messageDocRef = doc(db, "messages", messageId);

      await updateDoc(messageDocRef, {
        text: deleteMessage,
      });
    }
  };
  //  --------------------------------------------------------- Nachricht löschen als ADMIN ---------------------------------------------------------
  const handleAdminClear = async (messageId) => {
    if (isRoleAdmin) {
      const messageDocRef = doc(db, "messages", messageId);
      await updateDoc(messageDocRef, {
        text: adminDeleteMessage,
      });
    }
  };

  const handleDeleteFromDB = async (messageId) => {
    if (isRoleAdmin) {
      const messageDocRef = doc(db, "messages", messageId);
      await deleteDoc(messageDocRef);
    }
  };

  // --------------------------------------------------------- Verbindungsstatus ---------------------------------------------------------
  const handleOnlineStatusChange = () => {
    setIsOnline(navigator.onLine);
    if (navigator.onLine) {
      setOfflineMessage("");
    } else {
      setOfflineMessage("Keine Internetverbindung");
    }
  };
  const formatUsername = (username) => {
    return username.toLowerCase().replace(/\s+/g, ""); //oder '_'
  };

  // --------------------------------------------------------- ADMIN FUNKTIONEN ---------------------------------------------------------
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleDeleteAllMessages = async () => {
    if (confirmDelete) {
      const queryMessages = query(messagesRef, where("room", "==", room));
      const snapshot = await getDocs(queryMessages);
      const batch = writeBatch(db);
      snapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
      await batch.commit();
      setConfirmDelete(false);
    } else {
      setConfirmDelete(true);
    }
  };

  const downloadChatAsTextFile = () => {
    const chatText = messages
      .map((message) => {
        const timestamp = message.createdAt
          ? new Date(message.createdAt.toDate()).toLocaleString(undefined, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "";
        return `${timestamp} - ${message.user}: ${message.text}`;
      })
      .join("\n");

    const blob = new Blob([chatText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = saveFileName + room + "_.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  // UI ---------------------------------------------------------

  return (
    <div className="chat-app fadeIn01">
      {/* Header */}
      <div className="header">
        <p>{room}</p>
      </div>
      {/* Offline Status */}
      {offlineMessage && (
        <div className="offline-message fadeIn1">{offlineMessage}</div>
      )}
      <div className="messages fadeIn1">
        {messages
          .slice()
          .reverse()
          .map((message) => (
            <div
              className={`message fadeIn02 ${
                message.text === deleteMessage ? "deleted-message fadeIn1" : ""
              } ${
                message.text === adminDeleteMessage
                  ? "admin-deleted-message fadeIn1"
                  : ""
              }`}
              key={message.id}
            >
              {/* Datum */}

              {message.createdAt && (
                <span className="timestamp fadeIn1">
                  {new Date(message.createdAt?.toDate()).toLocaleString(
                    undefined,
                    {
                      hour: "2-digit",
                      minute: "2-digit",
                    }
                  )}{" "}
                  {new Date(message.createdAt?.toDate()).toLocaleString(
                    undefined,
                    {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                    }
                  )}
                </span>
              )}
              {/* Delete Button */}
              {!isRoleAdmin &&
                message.user === auth.currentUser.displayName && (
                  <button
                    className="delete"
                    onClick={() => handleDelete(message.id)}
                    title="Nachricht löschen"
                  >
                    {deleteSymbol}
                  </button>
                )}
              {/* Admin Delete */}
              {isRoleAdmin && (
                <button
                  className="admindelete"
                  onClick={() => handleAdminClear(message.id)}
                  title="Nachricht löschen"
                >
                  {deleteSymbol}
                </button>
              )}
              {/* Admin Delete DB */}
              {isRoleAdmin && (
                <button
                  className="adminDBdelete"
                  onClick={() => handleDeleteFromDB(message.id)}
                  title="Nachricht aus DB löschen"
                >
                  {adminDeleteSymbol}
                </button>
              )}
              {/* Username */}
              <span
                className={`user ${
                  message.user === auth.currentUser.displayName
                    ? "own-user"
                    : ""
                }`}
                style={{ color: ProfileColor(message.user) }}
                title={
                  isRoleAdmin
                    ? "name: " +
                      message.user +
                      " | email: " +
                      message.userEmail +
                      " | uid: " +
                      message.uid
                    : ""
                }
              >
                {/* Username Name */}
                <span>{formatUsername(message.user)}</span>
              </span>

              {/* Nachricht */}
              <p className="prefixSymbol">»</p>
              {isRoleAdmin || isRoleVip ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {message.text}
                </ReactMarkdown>
              ) : (
                <p>{message.text}</p>
              )}
            </div>
          ))}
        <div ref={messagesEndRef} className="messageText"></div>
      </div>
      <div className="controls-fixed">
        {/* Session Infos-Admin */}
        <div className="sessionInfo fadeIn06">
          {isRoleAdmin && (
            <p className="sessionInfoBtn" onClick={downloadChatAsTextFile}>
              <FontAwesomeIcon icon={["fas", "download"]} /> SPEICHERN
            </p>
          )}
          {isRoleAdmin && (
            <p className="sessionInfoBtn" onClick={handleDeleteAllMessages}>
              {confirmDelete ? (
                <>
                  <FontAwesomeIcon icon={["fas", "trash"]} /> BESTÄTIGEN
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={["fas", "trash"]} /> LEEREN
                </>
              )}
            </p>
          )}
        </div>
        {/* Session Infos */}
        <div className="sessionInfo fadeIn06">
          <p>
            {isOnline ? (
              <span>
                <FontAwesomeIcon icon={["fas", "user"]} />{" "}
                <b style={personalColor}>
                  {formatUsername(auth.currentUser.displayName)}
                </b>{" "}
                [
                {isRoleAdmin
                  ? "Admin"
                  : isRoleVip
                  ? "VIP"
                  : isMuted
                  ? "MUTED"
                  : "User"}
                ]
              </span>
            ) : (
              "Offline"
            )}
          </p>
          <p>
            <FontAwesomeIcon icon={["fas", "comments"]} /> Raum{" "}
            <b style={personalColor}>{room}</b>
          </p>
          <p>
            <FontAwesomeIcon icon={["fas", "comment"]} />{" "}
            <b style={personalColor}>{messages.length}</b>{" "}
            <FontAwesomeIcon icon={["fas", "users"]} />{" "}
            <b style={personalColor}>{uniqueUsers}</b>
          </p>
        </div>
        {/* Message Form */}
        <form onSubmit={handleSubmit} className="new-message-form">
          <input
            className="new-message-input"
            placeholder="Nachricht eingeben"
            onChange={handleInputChange}
            value={newMessage}
            maxLength={maxWriteLength}
          />

          <div className="char-count">
            <span>{charCount}</span> / {maxWriteLength}
          </div>
          <button type="submit" className="send-button">
            <FontAwesomeIcon icon={["fas", "paper-plane"]} />
          </button>
        </form>
      </div>
    </div>
  );
};
